import * as React from "react";
import * as React44ms from "../../../Util/form-utils/react-44ms";
import * as ReactQuery from "react-query";
import { UseQueryResult } from "react-query";
import axios from "axios";

import { DataSource_CheckoutRegions_QueryKey } from "./data-source-checkout-regions-query-key-factory";
import {
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
} from "../../graphql";
import { useLocalizationProvider } from "../localization-provider";

export interface CheckoutRegions_Response {
  id: any;
  two_letter_abbreviation: string;
  three_letter_abbreviation: string;
  full_name_locale: string;
  full_name_english: string;
  available_regions: {
    id: any;
    code: /* Uppercase< */ string /* > */;
    name: string;
  }[];
}

interface DataSourceCheckoutRegionsQueryConsumerInterface {
  data: UseQueryResult<CheckoutRegions_Response>["data"];
  status: UseQueryResult<CheckoutRegions_Response>["status"];
}
const defaultValue =
  {} as unknown as DataSourceCheckoutRegionsQueryConsumerInterface;
const DataSourceCheckoutRegionsQueryContext = React.createContext(defaultValue);
const DataSourceCheckoutRegionsQueryContext_44 = React44ms.React.createContext(
  defaultValue,
  DataSourceCheckoutRegionsQueryContext
);

export interface InitialStateMinimumRequirements_CheckoutRegions_DataSource
  extends InitialStateMinimumRequirements_GraphQl {}

export interface DataSourceCheckoutRegionsQueryProviderProps {
  countryCode: string;

  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_CheckoutRegions_DataSource;
  };
}
export const DataSourceCheckoutRegionsQueryProvider: React.FunctionComponent<
  DataSourceCheckoutRegionsQueryProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const { countryCode } = useLocalizationProvider();

  const queryKey = React.useMemo(() => {
    return DataSource_CheckoutRegions_QueryKey.checkoutRegions(props);
  }, [props, props.countryCode]);

  const queryFn: ReactQuery.QueryFunction<
    CheckoutRegions_Response,
    typeof queryKey
  > = async (context: ReactQuery.QueryFunctionContext<typeof queryKey>) => {
    const [baseQuery, desiredRegions] = context.queryKey;

    const { countryCode } = desiredRegions;

    let gqlQuery = `query {
      country(id: "${countryCode.toUpperCase()}") {
          id
          two_letter_abbreviation
          three_letter_abbreviation
          full_name_locale
          full_name_english
          available_regions {
              id
              code
              name
          }
      }
    }`;

    const headers = {
      "Content-Type": "application/json",
      Store: initialState.store_code,
    };

    const { data } = await axios.post<
      GraphqlWrappedData<"country", CheckoutRegions_Response>
    >("/graphql", { query: gqlQuery }, { headers });

    const { data: gqlResponseData } = data || {};
    const { country } = gqlResponseData || {};

    if (country.available_regions) {
      const availableRegionsWithoutDuplicates_idSet = new Set(
        country.available_regions.map((region) => region.code)
      );
      country.available_regions = Array.from(
        availableRegionsWithoutDuplicates_idSet.values()
      )
        .map((regionCode) =>
          country.available_regions.find((region) => region.code === regionCode)
        )
        .filter(Boolean) as CheckoutRegions_Response["available_regions"];
    }

    return country;
  };

  const { status, data, dataUpdatedAt, errorUpdatedAt, error, ...result } =
    ReactQuery.useQuery(queryKey, queryFn, { refetchOnWindowFocus: false });

  const value: DataSourceCheckoutRegionsQueryConsumerInterface = React.useMemo(
    () => ({
      status,
      data,
      dataUpdatedAt,
      errorUpdatedAt,
      error,
    }),
    [status, data, dataUpdatedAt, errorUpdatedAt, error]
  );
  return (
    <DataSourceCheckoutRegionsQueryContext.Provider value={value}>
      <DataSourceCheckoutRegionsQueryContext_44.Provider value={value}>
        {props?.children || []}
      </DataSourceCheckoutRegionsQueryContext_44.Provider>
    </DataSourceCheckoutRegionsQueryContext.Provider>
  );
};

export const useDataSourceCheckoutRegionsQuery = () => {
  const context = React.useContext(DataSourceCheckoutRegionsQueryContext);

  /**
   * Normally we do this pattern below for using any provider,
   * this here is a special case where
   * we want the defaultValue to be used when this provider isn't in the tree,
   * because it can be used when there is a fully formed request information packet,
   * which isn't guaranteed to be available immediately.
   */
  // if (context === defaultValue) {
  //   throw new Error(
  //     "useDataSourceCheckoutRegions must be used within a DataSourceCheckoutRegionsQueryProvider"
  //   );
  // }

  return context;
};

export const useDataSourceCheckoutRegionsQuery_44 = () => {
  const context = React44ms.React.useContext(
    DataSourceCheckoutRegionsQueryContext_44
  );

  /**
   * Normally we do this pattern below for using any provider,
   * this here is a special case where
   * we want the defaultValue to be used when this provider isn't in the tree,
   * because it can be used when there is a fully formed request information packet,
   * which isn't guaranteed to be available immediately.
   */
  // if (context === defaultValue) {
  //   throw new Error(
  //     "useDataSourceCheckoutRegions must be used within a DataSourceCheckoutRegionsQueryProvider"
  //   );
  // }

  return context;
};
